import { makeStyles } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Security';
import React, { useMemo } from 'react';
import {
  Datagrid,
  List,
  ReferenceManyField,
  Responsive,
  TextInput,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  Pagination,
  Loading,
  useGetList
} from 'react-admin';
import get from 'lodash/get';

import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from '../../core/components/ListTitleBar';
import StatusSelectField from '../../core/components/StatusSelectField';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import GroupCategoryDatagrid from './GroupCategoryDatagrid';
import { groupStatus } from './GroupLookups';

const useStyles = makeStyles({
  expandDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      }
    }
  },
  expandIconCell: {
    width: '14px',
    '& div': {
      width: '14px',
      padding: '0'
    },
    '& div:hover ': {
      backgroundColor: 'transparent'
    }
  },
  statusColumnStyle: {
    width: '68px'
  },
  root: {
    marginRight: 'auto'
  },
  selectRoot: {
    height: '17px'
  }
});

const useshowStyles = makeStyles({
  root: {
    padding: '16px 16px 10px 16px'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();
  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const GroupFilters = ({ loggedUserType, ...props }) => {
  const classes = useStyles();

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Group Name"
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
      />
      <TextInput
        label="Description"
        source="description"
        allowEmpty
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      {loggedUserType === 'SUPER_ADMIN' && (
        <SelectInput
          label="Status"
          source="status"
          choices={groupStatus}
          classes={{ root: classes.selectRoot }}
          allowEmpty
        />
      )}
    </Filter>
  );
};

const GroupTitle = props => {
  return (
    <ListTitleBar
      type="Permission Groups"
      title="Create New Group"
      createUrl="#/groups/create"
      icon={<GroupIcon />}
      hasCreatePermission={props.hasCreatePermission}
    />
  );
};

const CategoriesShow = props => {
  const classes = useshowStyles();
  const {
    handleGroupCategoryClick,
    permissions,
    isDisplayAdminCategoriesOn,
    ...rest
  } = props;

  const filter = useMemo(() => {
    return isDisplayAdminCategoriesOn ? {} : { subType: 'REPORTING' };
  }, [isDisplayAdminCategoriesOn]);

  return (
    <div className={classes.root}>
      <ReferenceManyField
        label=""
        reference="groupcategories"
        target="groupId"
        filter={filter}
        sort={{ field: 'categoryOrSubcategoryName', order: 'ASC' }}
        {...rest}
      >
        <GroupCategoryDatagrid
          handleGroupCategoryClick={handleGroupCategoryClick}
          expand={true}
          permissions={permissions}
        />
      </ReferenceManyField>
    </div>
  );
};

const GroupList = props => {
  const classes = useStyles();
  const {
    permissions = [],
    hasReadPermission,
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProfile,
    ...rest
  } = props;

  const {
    data: tenantConfigurations = {},
    loading: loadingTenantConfigurations
  } = useGetList('tenantconfigurations', null, null, {
    nameOrCategory: 'display_admin_categories'
  });

  const isDisplayAdminCategoriesOn = useMemo(() => {
    return get(Object.values(tenantConfigurations), '[0].value', '0') === '1';
  }, [tenantConfigurations]);

  if (loadingTenantConfigurations) return <Loading />;

  const handleGroupCategoryClick = groupCategoryId => {
    let categoryId = groupCategoryId || '';
    if (categoryId.includes(':')) {
      categoryId = groupCategoryId.split(':')[1];
    }

    return `/categories/${categoryId}`;
  };

  const groupsFilter = {
    adminGroup: false,
    status: 'ACTIVE'
  };

  if (
    userProfile &&
    (userProfile.userType === 'SUPER_ADMIN' ||
      (userProfile.userType === 'FUND_ADMIN' &&
        permissions.includes('AUTH_READ_GROUP_NA')))
  ) {
    delete groupsFilter.status;
  }

  return (
    <List
      {...rest}
      sort={{ field: 'name', order: 'ASC' }}
      exporter={false}
      title={<GroupTitle hasCreatePermission={hasCreatePermission} />}
      filters={
        <GroupFilters loggedUserType={userProfile && userProfile.userType} />
      }
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      filter={groupsFilter}
      bulkActionButtons={false}
      actions={<Actions />}
      empty={<Empty />}
      syncWithLocation
    >
      <Responsive
        small={
          <SimpleList
            primaryText={record => record.name}
            secondaryText={record => record.id}
            tertiaryText={() => ''}
          />
        }
        medium={
          <Datagrid
            classes={{
              thead: classes.expandDataGridHead,
              expandIconCell: classes.expandIconCell
            }}
            expand={
              <CategoriesShow
                handleGroupCategoryClick={handleGroupCategoryClick}
                permissions={permissions}
                isDisplayAdminCategoriesOn={isDisplayAdminCategoriesOn}
              />
            }
          >
            <LinkField label="Group Name" source="name" />
            <TextField label="Description" source="description" />
            <StatusSelectField
              label="Status"
              source="status"
              choices={groupStatus}
              headerClassName={classes.statusColumnStyle}
            />
          </Datagrid>
        }
      />
    </List>
  );
};

export default GroupList;
